import { supabase } from '../lib/supabase.js';
import { openEventPhotos } from '../lib/event-photos.js';

const WEEKDAYS = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];

function fmtDate(d) {
    const dt = new Date(d);
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}
function fmtWeekday(d) { return WEEKDAYS[new Date(d).getDay()]; }
function fmtTime(t) { return t ? t.slice(0, 5) : ''; }
function fmtTimeRange(a, b) { return [fmtTime(a), fmtTime(b)].filter(Boolean).join(' – '); }
function fmtDateRange(a, b) {
    if (!b || b === a) return fmtDate(a);
    const da = new Date(a), db = new Date(b);
    if (da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth()) {
        return `${da.getDate()}. – ${db.getDate()}. ${da.getMonth() + 1}. ${da.getFullYear()}`;
    }
    return `${fmtDate(a)} – ${fmtDate(b)}`;
}

function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function techLines(place) {
    if (!place) return '';
    const lines = [];
    if (place.environment) lines.push(esc(place.environment.charAt(0).toUpperCase() + place.environment.slice(1)));
    const power = [place.power_type, place.power_side].filter(Boolean).join(' · ');
    if (power) lines.push(esc(power));
    const cables = [];
    if (place.cable_400v && place.cable_400v !== 'žádný') cables.push(`400 V: ${esc(place.cable_400v)}`);
    if (place.cable_220v && place.cable_220v !== 'žádný') cables.push(`220 V: ${esc(place.cable_220v)}`);
    if (cables.length) lines.push('Prodlužovák ' + cables.join(' · '));
    if (place.pa_variant) lines.push(esc(place.pa_variant));
    if (place.top_box_angle) lines.push(`TOP: ${esc(place.top_box_angle)}`);
    const subInfo = [];
    if (place.sub_placement) subInfo.push(esc(place.sub_placement));
    if (place.sub_delay_ms) subInfo.push(`${place.sub_delay_ms}ms`);
    if (place.sub_notes) subInfo.push(esc(place.sub_notes));
    if (subInfo.length) lines.push(`SUB: ${subInfo.join(' · ')}`);
    const tags = (place.moving_options || []).map(o => `<span class="tag">${esc(o)}</span>`).join(' ');
    const carpet = place.has_carpet ? '<br><span class="koberec-tag">Koberec na místě</span>' : '';
    return lines.join('<br>') + (tags ? '<br>' + tags : '') + carpet;
}

function renderTechCell(place) {
    if (!place) return '<span style="color:var(--text-muted);">—</span>';
    const content = techLines(place);
    if (!content.trim()) return '<span style="color:var(--text-muted);">—</span>';
    return `<details class="tech-toggle"><summary>Technika</summary><div class="tech-body">${content}</div></details>`;
}

// ===== Mobilní kompaktní karty historie =====
function hLine(k, v) {
    return `<div class="evc-line"><span class="evc-k">${k}</span>${v}</div>`;
}

function renderGigCard(ev) {
    const placeName = ev.place?.name || '—';
    const detail = [];
    const tr = fmtTimeRange(ev.time_start, ev.time_end);
    if (tr) detail.push(hLine('Začátek – konec', esc(tr)));
    const meet = [];
    if (ev.meeting_time)      meet.push(`Sraz ${fmtTime(ev.meeting_time)}`);
    if (ev.departure_pilsen)  meet.push(`Odjezd Plzeň ${fmtTime(ev.departure_pilsen)}`);
    if (ev.departure_stribro) meet.push(`Odjezd Stříbro ${fmtTime(ev.departure_stribro)}`);
    if (meet.length) detail.push(hLine('Sraz / odjezd', meet.join(' · ')));
    if (ev.organizer) detail.push(hLine('Pořadatel', esc(ev.organizer) + (ev.organizer_phone ? ' · ' + esc(ev.organizer_phone) : '')));
    detail.push(hLine('Hráli', renderHraliCell(ev.musicians)));
    if (ev.notes) detail.push(hLine('Poznámky', esc(ev.notes)));
    const tech = techLines(ev.place);
    if (tech) detail.push(hLine('Technika', tech));

    return `
    <details class="hcard">
        <summary class="hcard-sum">
            <span class="hcard-date">${fmtDate(ev.event_date)}</span>
            <span class="hcard-place">${esc(placeName)}</span>
        </summary>
        <div class="hcard-body">
            ${detail.join('')}
            <button class="btn btn-sm hcard-gallery" data-event-gallery="${ev.id}"
                    data-event-label="${esc(fmtDate(ev.event_date) + ' · ' + placeName)}">
                📷 Fotogalerie akce
            </button>
        </div>
    </details>`;
}

function renderBlockCard(ev) {
    const name = ev.blocked_user?.name || 'Někdo';
    return `
    <div class="hcard hcard-block">
        <span class="hcard-date">${fmtDateRange(ev.event_date, ev.event_date_end)}</span>
        <span class="hcard-place">Volno · ${esc(name)} – ${esc(ev.block_reason || 'volno')}</span>
    </div>`;
}

function renderHraliCell(musicians) {
    if (!musicians || musicians.length === 0) {
        return '<span style="color:var(--text-muted);font-style:italic;">bez záznamu</span>';
    }
    return musicians
        .map((em) => {
            const p = em.musician;
            const host = p?.musician_role === 'stalý host'
                ? ` <span style="font-size:9px;background:var(--warning);color:white;padding:0 4px;border-radius:3px;font-weight:700;">${esc(em.share_percent || p.share_percent)}%</span>`
                : '';
            return `<span style="font-weight:500;">${esc(p?.name || '?')}${host}</span>`;
        })
        .join('<span style="color:var(--border-strong);margin:0 4px;">·</span>');
}

function renderGigRow(ev) {
    return `
        <tr class="past">
            <td class="date-cell" data-label="Datum">
                ${fmtDate(ev.event_date)}
                <span class="weekday">${fmtWeekday(ev.event_date)}</span>
            </td>
            <td class="time-cell" data-label="Začátek / Konec">${esc(fmtTimeRange(ev.time_start, ev.time_end))}</td>
            <td class="place-cell" data-label="Místo">${esc(ev.place?.name || '—')}</td>
            <td class="time-cell" data-label="Sraz">${esc(fmtTime(ev.meeting_time))}</td>
            <td class="time-cell" data-label="Odjezd Plzeň">${esc(fmtTime(ev.departure_pilsen))}</td>
            <td class="time-cell" data-label="Odjezd Stříbro">${esc(fmtTime(ev.departure_stribro))}</td>
            <td data-label="Pořadatel">${esc(ev.organizer || '—')}${ev.organizer_phone ? `<br><span style="color:var(--text-muted);font-size:12px;">${esc(ev.organizer_phone)}</span>` : ''}</td>
            <td class="hrali-cell" data-label="Hráli" style="min-width:160px;font-size:13px;line-height:1.6;">
                ${renderHraliCell(ev.musicians)}
            </td>
            <td class="notes-cell" data-label="Poznámky">${esc(ev.notes || '')}</td>
            <td class="tech-cell" data-label="Technické">${renderTechCell(ev.place)}</td>
        </tr>
    `;
}

function renderBlockRow(ev) {
    const name = ev.blocked_user?.name || 'Někdo';
    return `
        <tr class="blocked-row">
            <td class="date-cell" data-label="Datum">
                ${fmtDateRange(ev.event_date, ev.event_date_end)}
                <span class="weekday">${fmtWeekday(ev.event_date)}</span>
            </td>
            <td colspan="9" class="block-info" data-label="Blokace">
                <span class="badge blocked" style="margin-right:8px; vertical-align:middle;">Volno</span>
                <strong>${esc(name)}</strong> – <em>${esc(ev.block_reason || 'volno')}</em>
                <span class="block-reason">(termín byl blokován)</span>
            </td>
        </tr>
    `;
}

export async function renderHistorie(container) {
    container.innerHTML = '<div class="state-banner">Načítám historii…</div>';

    const today = new Date().toISOString().slice(0, 10);

    const { data, error } = await supabase
        .from('events')
        .select(`
            *,
            place:places(*),
            blocked_user:profiles!events_blocked_for_fkey(id, name),
            musicians:event_musicians(
                share_percent,
                km,
                wage_override,
                musician:profiles(id, name, musician_role, share_percent)
            )
        `)
        .lt('event_date', today)
        .order('event_date', { ascending: false });

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba načtení: ${esc(error.message)}</div>`;
        console.error(error);
        return;
    }

    const rows = (data || []).map((ev) => (ev.type === 'block' ? renderBlockRow(ev) : renderGigRow(ev))).join('');
    const cards = (data || []).map((ev) => (ev.type === 'block' ? renderBlockCard(ev) : renderGigCard(ev))).join('');

    container.innerHTML = `
        <div class="filters">
            <input class="search-input" type="search" placeholder="Hledat v historii...">
            <select class="filter-select">
                <option>Všechny roky</option>
                <option>${new Date().getFullYear()}</option>
                <option>${new Date().getFullYear() - 1}</option>
            </select>
        </div>

        <div class="table-wrap">
            <table class="events-table history-table">
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Začátek / Konec</th>
                        <th>Místo konání</th>
                        <th>Sraz</th>
                        <th>Odjezd<br>Plzeň</th>
                        <th>Odjezd<br>Stříbro</th>
                        <th>Pořadatel</th>
                        <th>Hráli</th>
                        <th>Poznámky</th>
                        <th>Technické parametry</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows || `<tr><td colspan="10" style="text-align:center;padding:30px;color:var(--text-muted);">
                        Historie je zatím prázdná. Proběhlé akce se sem přidají automaticky po datu konání.
                    </td></tr>`}
                </tbody>
            </table>
        </div>

        <div class="historie-cards">
            ${cards || '<div class="state-banner" style="margin:0;">Historie je zatím prázdná.</div>'}
        </div>

        <div class="footer-note">
            Načteno z Supabase. <strong>${data?.length || 0}</strong> proběhlých záznamů (od nejnovější).
            ${data?.length === 0 ? '<br>Tip: pro test přidej v Supabase SQL Editor jednu akci s datem v minulosti.' : ''}
        </div>
    `;

    // Fotogalerie akce (z rozbalené karty historie)
    container.querySelectorAll('[data-event-gallery]').forEach((btn) => {
        btn.addEventListener('click', () => {
            openEventPhotos(btn.dataset.eventGallery, btn.dataset.eventLabel);
        });
    });
}
